import React, { useState, useRef, useEffect, useCallback } from 'react';
import { cn } from '../../utils/cn';

interface InlineRenameInputProps {
  initialValue: string;
  onSubmit: (newName: string) => void;
  onCancel: () => void;
  isFolder?: boolean;
}

// Invalid filename characters for Windows/Unix compatibility
const INVALID_CHARS_REGEX = /[\/\\:*?"<>|]/;

// Get the file extension from a filename
const getExtension = (filename: string): string => {
  const lastDot = filename.lastIndexOf('.');
  if (lastDot === -1 || lastDot === 0) return '';
  return filename.substring(lastDot);
};

export const InlineRenameInput: React.FC<InlineRenameInputProps> = ({
  initialValue,
  onSubmit,
  onCancel,
  isFolder = false,
}) => {
  const [value, setValue] = useState(initialValue);
  const [isValid, setIsValid] = useState(true);
  const [extensionWarning, setExtensionWarning] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const initialExtension = useRef(getExtension(initialValue));

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, []);

  const validate = useCallback((name: string): boolean => {
    // Name cannot be empty
    if (!name.trim()) return false;
    // Name cannot contain invalid characters
    if (INVALID_CHARS_REGEX.test(name)) return false;
    return true;
  }, []);

  const checkExtensionChange = useCallback((name: string): boolean => {
    if (isFolder) return false;
    const newExtension = getExtension(name);
    return initialExtension.current !== newExtension;
  }, [isFolder]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setValue(newValue);
    setIsValid(validate(newValue));
    setExtensionWarning(checkExtensionChange(newValue));
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (isValid && value.trim()) {
        onSubmit(value.trim());
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onCancel();
    }
  };

  const handleBlur = () => {
    onCancel();
  };

  return (
    <div className="relative flex-1 min-w-0">
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        className={cn(
          'w-full px-1 py-0.5 text-sm bg-zinc-800 border rounded outline-none',
          'text-zinc-200',
          isValid
            ? extensionWarning
              ? 'border-yellow-500'
              : 'border-blue-500'
            : 'border-red-500'
        )}
        spellCheck={false}
        autoComplete="off"
      />
      {extensionWarning && isValid && (
        <div className="absolute left-0 top-full mt-1 px-2 py-1 text-xs bg-yellow-900/90 text-yellow-200 rounded shadow-lg z-50 whitespace-nowrap">
          File extension will change
        </div>
      )}
    </div>
  );
};
